import * as Sqrl from 'squirrelly';

import {getSizedImageUrl} from '../vendor/theme-scripts/theme-images';
import {trapFocus} from '../vendor/theme-scripts/theme-a11y';
import FetchError from '../util/fetch-error';
import {formatPrices} from '../util/format-prices';
import debounce from '../util/debounce';

const selectors = {
  wrapper: '[data-search-popdown-wrap]',
  results: '[data-predictive-search-results]',
  input: '[data-predictive-search-input]',
  productTemplate: '[data-search-product-template]',
  otherTemplate: '[data-search-other-template]',
  titleTemplate: '[data-predictive-search-title-template]',
  ariaTemplate: '[data-predictive-search-aria-template]',
  productTitleWrapper: '[data-product-title-wrap]',
  productWrapper: '[data-product-wrap]',
  collectionWrapper: '[data-collection-wrap]',
  articleWrapper: '[data-article-wrap]',
  pageWrapper: '[data-page-wrap]',
  ariaWrapper: '[data-predictive-search-aria]',
  outerWrapper: '[data-popdown-outer]',
  loader: '[data-loading-indicator]',
  dirtyClass: 'dirty',
  noResults: 'search--empty',
};

let sections = {};

Sqrl.filters.define('animationDelay', function (index) {
  return index * 90 + 10;
});

class SearchPredictive {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = this.wrapper.querySelector(selectors.input);
    this.loader = this.wrapper.querySelector(selectors.loader);
    this.results = this.wrapper.querySelector(selectors.results);
    this.outer = this.input.closest(selectors.outerWrapper);

    this.productTemplate = this.wrapper.querySelector(selectors.productTemplate).innerHTML;
    this.otherTemplate = this.wrapper.querySelector(selectors.otherTemplate).innerHTML;
    this.titleTemplate = this.wrapper.querySelector(selectors.titleTemplate).innerHTML;
    this.ariaTemplate = this.wrapper.querySelector(selectors.ariaTemplate).innerHTML;

    this.productTitleWrapper = this.results.querySelector(selectors.productTitleWrapper);
    this.productWrapper = this.results.querySelector(selectors.productWrapper);
    this.collectionWrapper = this.results.querySelector(selectors.collectionWrapper);
    this.articleWrapper = this.results.querySelector(selectors.articleWrapper);
    this.pageWrapper = this.results.querySelector(selectors.pageWrapper);
    this.ariaWrapper = this.results.querySelector(selectors.ariaWrapper);

    this.initSearch();
  }

  initSearch() {
    this.input.addEventListener(
      'input',
      debounce(
        function (event) {
          const val = event.target.value;
          if (val && val.length > 1) {
            this.loader.style.display = 'block';
            this.render(val);
          } else {
            this.resetTemplates();
            this.outer.classList.remove(selectors.dirtyClass);
          }
        }.bind(this),
        300
      )
    );
    this.input.addEventListener('clear', this.reset.bind(this));
  }

  render(terms) {
    let resources = '';
    resources += window.theme.settings.search_products ? 'product,' : '';
    resources += window.theme.settings.search_collections ? 'collection,' : '';
    resources += window.theme.settings.search_articles ? 'article,' : '';
    resources += window.theme.settings.search_pages ? 'page,' : '';
    resources = resources.slice(0, -1);
    const serialized = `/search/suggest.json?q=${terms}&resources[type]=${resources}&resources[options][unavailable_products]=last`;
    fetch(serialized)
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.resetTemplates();
        this.outer.classList.add(selectors.dirtyClass);
        const results = response.resources.results;
        const combined = [];
        for (const key in results) {
          if ({}.hasOwnProperty.call(results, key)) {
            combined.push(...results[key]);
          }
        }
        if (combined.length) {
          this.outer.classList.remove(selectors.noResults);
          this.injectOther(results);
          this.injectProduct(results.products);
        } else {
          this.noResults(terms);
        }
        this.injectAria(terms, combined);
        trapFocus(this.outer, {elementToFocus: this.input});
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.loader.style.display = 'none';
      });
  }

  injectAria(terms, combined) {
    let title = window.theme.strings.noResultsFor;
    let count = null;
    if (combined.length) {
      count = combined.length;
      title = window.theme.strings.resultsFor;
    }
    this.ariaWrapper.innerHTML = Sqrl.render(this.ariaTemplate, {
      count: count,
      title: title,
      query: terms,
    });
  }

  noResults() {
    this.resetTemplates();
    this.outer.classList.add(selectors.dirtyClass);
    this.outer.classList.add(selectors.noResults);
  }

  resetTemplates() {
    this.productTitleWrapper.innerHTML = '';
    this.collectionWrapper.innerHTML = '';
    this.articleWrapper.innerHTML = '';
    this.productWrapper.innerHTML = '';
    this.pageWrapper.innerHTML = '';
    this.ariaWrapper.innerHTML = '';
  }

  reset() {
    this.resetTemplates();
    this.outer.classList.remove(selectors.dirtyClass);
    this.outer.classList.remove(selectors.noResults);
    this.input.val = '';
  }

  injectOther(results) {
    this.productTitleWrapper.innerHTML += Sqrl.render(this.titleTemplate, {
      title: window.theme.strings.products,
      count: results.products.length,
    });
    if (results.collections && results.collections.length) {
      this.collectionWrapper.innerHTML += Sqrl.render(this.titleTemplate, {
        title: window.theme.strings.collections,
        count: results.collections.length,
      });
      this.collectionWrapper.innerHTML += Sqrl.render(this.otherTemplate, results.collections);
    }
    if (results.pages && results.pages.length) {
      this.pageWrapper.innerHTML += Sqrl.render(this.titleTemplate, {
        title: window.theme.strings.pages,
        count: results.pages.length,
      });
      this.pageWrapper.innerHTML += Sqrl.render(this.otherTemplate, results.pages);
    }
    if (results.articles && results.articles.length) {
      this.articleWrapper.innerHTML += Sqrl.render(this.titleTemplate, {
        title: window.theme.strings.articles,
        count: results.articles.length,
      });
      this.articleWrapper.innerHTML += Sqrl.render(this.otherTemplate, results.articles);
    }
  }

  injectProduct(products) {
    let formatted = [];
    products.forEach((p) => {
      let product = p;
      product = formatPrices(product);
      product.image = null;
      if (product.featured_image) {
        product.thumb = getSizedImageUrl(product.featured_image.url, '360x360');
      }
      formatted.push(product);
    });
    const productHTML = Sqrl.render(this.productTemplate, formatted);
    this.productWrapper.innerHTML += productHTML;
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

const searchResultsGlobal = {
  onLoad() {
    sections[this.id] = [];
    const els = document.querySelectorAll(selectors.wrapper);
    els.forEach((el) => {
      sections[this.id].push(new SearchPredictive(el));
    });
  },
  onUnload: function () {
    sections[this.id].forEach((el) => {
      if (typeof el.unload === 'function') {
        el.unload();
      }
    });
  },
};

export {searchResultsGlobal};
